import { useEffect, useState } from "react";
import { getHealthAll, HealthResponse, HealthCheck } from "../services/health";

const STATUS_META = (status: string) => {
  const s = status?.toLowerCase();
  if (s === "healthy")   return { color: "#16a34a", bg: "#f0fdf4", border: "#bbf7d0", icon: "bi-check-circle-fill",  label: "Healthy" };
  if (s === "degraded")  return { color: "#d97706", bg: "#fffbeb", border: "#fde68a", icon: "bi-exclamation-circle-fill", label: "Degraded" };
  return                        { color: "#dc2626", bg: "#fef2f2", border: "#fecaca", icon: "bi-x-circle-fill",       label: "Unhealthy" };
};

const CHECK_META: Record<string, { icon: string; label: string; iconColor: string }> = {
  "mysql":   { icon: "bi-database-fill",       label: "MySQL",   iconColor: "#0ea5e9" },
  "aws-s3":  { icon: "bi-cloud-fill",          label: "AWS S3",  iconColor: "#f97316" },
  "memory":  { icon: "bi-memory",              label: "Bellek",  iconColor: "#8b5cf6" },
};

const fallbackMeta = { icon: "bi-gear-fill", label: "", iconColor: "#6b7280" };

function MemoryBar({ allocated, threshold }: { allocated: number; threshold: number }) {
  const pct = Math.min((allocated / threshold) * 100, 100);
  const color = pct > 80 ? "#dc2626" : pct > 60 ? "#d97706" : "#16a34a";
  return (
    <div className="aws-mem-bar-wrap">
      <div className="aws-mem-bar-track">
        <div className="aws-mem-bar-fill" style={{ width: `${pct}%`, background: color }} />
      </div>
      <span className="aws-mem-bar-label" style={{ color }}>{pct.toFixed(0)}%</span>
    </div>
  );
}

function CheckCard({ check }: { check: HealthCheck }) {
  const sm  = STATUS_META(check.status);
  const cm  = CHECK_META[check.name] ?? { ...fallbackMeta, label: check.name };
  const isS3  = check.name === "aws-s3";
  const isMem = check.name === "memory";

  return (
    <div className="aws-check-card" style={{ borderColor: sm.border }}>
      {/* Card header */}
      <div className="aws-check-card__header">
        <div className="aws-check-icon" style={{ background: `${cm.iconColor}18`, color: cm.iconColor }}>
          <i className={`bi ${cm.icon}`}></i>
        </div>
        <div className="aws-check-info">
          <div className="aws-check-name">{cm.label || check.name}</div>
          {check.description && <div className="aws-check-desc">{check.description}</div>}
        </div>
        <div className="aws-check-right">
          <span className="aws-check-status" style={{ background: sm.bg, color: sm.color, border: `1px solid ${sm.border}` }}>
            <i className={`bi ${sm.icon} me-1`}></i>{sm.label}
          </span>
          <span className="aws-check-duration">
            <i className="bi bi-stopwatch me-1"></i>{check.duration}
          </span>
        </div>
      </div>

      {/* S3 data */}
      {isS3 && check.data && (
        <div className="aws-check-data">
          <div className="aws-data-row">
            <span className="aws-data-key"><i className="bi bi-hdd-stack-fill me-1"></i>Bucket</span>
            <span className="aws-data-val">{check.data["Bucket"]}</span>
          </div>
          <div className="aws-data-row">
            <span className="aws-data-key"><i className="bi bi-geo-alt-fill me-1"></i>Bölge</span>
            <span className="aws-data-val">{check.data["Location"]}</span>
          </div>
        </div>
      )}

      {/* Memory data */}
      {isMem && check.data && (
        <div className="aws-check-data">
          <div className="aws-data-row aws-data-row--full">
            <span className="aws-data-key">Bellek Kullanımı</span>
            <MemoryBar
              allocated={check.data["AllocatedMB"] as number}
              threshold={check.data["ThresholdMB"] as number}
            />
          </div>
          <div className="aws-data-row">
            <span className="aws-data-key">Ayrılan</span>
            <span className="aws-data-val">{check.data["AllocatedMB"]} MB</span>
          </div>
          <div className="aws-data-row">
            <span className="aws-data-key">Eşik</span>
            <span className="aws-data-val">{check.data["ThresholdMB"]} MB</span>
          </div>
          <div className="aws-data-row">
            <span className="aws-data-key">GC Gen0</span>
            <span className="aws-data-val">{check.data["Gen0Collections"]}</span>
          </div>
          <div className="aws-data-row">
            <span className="aws-data-key">GC Gen1</span>
            <span className="aws-data-val">{check.data["Gen1Collections"]}</span>
          </div>
          <div className="aws-data-row">
            <span className="aws-data-key">GC Gen2</span>
            <span className="aws-data-val">{check.data["Gen2Collections"]}</span>
          </div>
        </div>
      )}
    </div>
  );
}

function AwsPage() {
  const [health, setHealth]   = useState<HealthResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState("");
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());

  const fetchHealth = () => {
    setLoading(true);
    setError("");
    getHealthAll()
      .then((res) => { setHealth(res.data); setLastRefresh(new Date()); })
      .catch(() => setError("Sağlık durumu alınamadı."))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchHealth(); }, []);

  const overall = health ? STATUS_META(health.status) : null;

  return (
    <div className="aws-page">

      {/* Header */}
      <div className="aws-header">
        <div className="aws-header__left">
          <div className="aws-header__icon">
            <i className="bi bi-cloud-fill"></i>
          </div>
          <div>
            <h4 className="aws-header__title">AWS Yönetimi</h4>
            <p className="aws-header__subtitle">Sistem sağlık durumu</p>
          </div>
        </div>
        <button className="aws-refresh-btn" onClick={fetchHealth} disabled={loading}>
          <i className={`bi bi-arrow-clockwise${loading ? " aws-spin" : ""}`}></i>
          Yenile
        </button>
      </div>

      {/* Error */}
      {error && (
        <div className="aws-error">
          <i className="bi bi-exclamation-circle-fill me-2"></i>{error}
        </div>
      )}

      {/* Loading skeleton */}
      {loading && !health && (
        <div className="aws-skeleton-list">
          {[1, 2, 3].map(i => <div key={i} className="aws-skeleton-card" />)}
        </div>
      )}

      {/* Overall status banner */}
      {health && overall && (
        <>
          <div className="aws-overall" style={{ background: overall.bg, border: `1.5px solid ${overall.border}` }}>
            <div className="aws-overall__left">
              <i className={`bi ${overall.icon} aws-overall__icon`} style={{ color: overall.color }}></i>
              <div>
                <div className="aws-overall__label">Genel Durum</div>
                <div className="aws-overall__status" style={{ color: overall.color }}>{overall.label}</div>
              </div>
            </div>
            <div className="aws-overall__right">
              <div className="aws-overall__dur">
                <i className="bi bi-stopwatch me-1"></i>Toplam Süre
                <strong className="ms-1">{health.totalDuration}</strong>
              </div>
              <div className="aws-overall__time">
                Son güncelleme: {lastRefresh.toLocaleTimeString("tr-TR")}
              </div>
            </div>
          </div>

          {/* Check cards */}
          <div className="aws-checks-list">
            {health.checks.map((c) => <CheckCard key={c.name} check={c} />)}
          </div>
        </>
      )}
    </div>
  );
}

export default AwsPage;
